import { useState, useEffect, useRef } from 'react';
import { Search, X, Sprout } from 'lucide-react';

/**
 * PlantCatalogHeader - Search bar and sorting for plant catalog
 *
 * Includes:
 * - Debounced search input (300ms)
 * - Results count display (shows "X von Y" when filtered)
 * - Sorting dropdown
 *
 * NOTE: Filters moved to PlantCatalogSidebar component (left sidebar)
 *
 * @param {Object} props - Component props
 * @param {string} props.searchQuery - Current search query
 * @param {Function} props.onSearchChange - Callback for search changes
 * @param {string} props.sortBy - Current sort option
 * @param {Function} props.onSortChange - Callback for sort changes
 * @param {number} props.resultsCount - Number of results found
 * @param {number} props.totalCount - Total number of plants (for "X von Y" display)
 */
const PlantCatalogHeader = ({
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  resultsCount,
  totalCount = 0,
  gardenFilterActive = false,
  onToggleGardenFilter,
  hasGardenSettings = false
}) => {
  const [localSearch, setLocalSearch] = useState(searchQuery || '');
  const [prevSearchQuery, setPrevSearchQuery] = useState(searchQuery);
  const debounceTimerRef = useRef(null);

  // Debounced search (300ms)
  useEffect(() => {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (onSearchChange) {
        onSearchChange(localSearch);
      }
    }, 300);

    return () => clearTimeout(debounceTimerRef.current);
  }, [localSearch, onSearchChange]);

  // Sync with external search query changes — adjusted during render rather than
  // in an effect to avoid a setState-in-effect cascade.
  if (searchQuery !== prevSearchQuery) {
    setPrevSearchQuery(searchQuery);
    setLocalSearch(searchQuery || '');
  }

  // Handle search input change
  const handleSearchChange = (e) => {
    setLocalSearch(e.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setLocalSearch('');
  };

  // Determine if filters are active (results differ from total)
  const hasActiveFilters = totalCount > 0 && resultsCount !== totalCount;

  return (
    <div className="flex items-center justify-between gap-4 p-4 border-b border-gray-200 bg-white">
      {/* Search Input */}
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={localSearch}
          onChange={handleSearchChange}
          placeholder="Pflanzen suchen..."
          className="w-full pl-10 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          aria-label="Pflanzen suchen"
        />
        {localSearch && (
          <button
            onClick={handleClearSearch}
            aria-label="Suche löschen"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors duration-200"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* "Für meinen Garten" toggle button - only shown when garden is configured */}
      {hasGardenSettings && (
        <button
          onClick={onToggleGardenFilter}
          aria-pressed={gardenFilterActive}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors duration-200 whitespace-nowrap ${
            gardenFilterActive
              ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
              : 'bg-white border-gray-300 text-gray-700 hover:border-emerald-400'
          }`}
        >
          <Sprout className="w-4 h-4" />
          Für meinen Garten
        </button>
      )}

      {/* Results Count - Enhanced Display */}
      <div className="text-sm text-gray-600 whitespace-nowrap">
        {totalCount === 0 ? (
          // Loading state or no data
          <span>—</span>
        ) : hasActiveFilters ? (
          // Filters active - show "X von Y Pflanzen gefunden"
          <span>
            <span className="font-semibold">{resultsCount}</span>
            {' von '}
            <span className="font-semibold">{totalCount}</span>
            {' Pflanzen gefunden'}
          </span>
        ) : (
          // No filters - show "X Pflanzen"
          <span>
            <span className="font-semibold">{totalCount}</span>
            {' '}
            {totalCount === 1 ? 'Pflanze' : 'Pflanzen'}
          </span>
        )}
      </div>

      {/* Sort Dropdown */}
      <div className="flex items-center gap-2 whitespace-nowrap">
        <span className="text-xs text-gray-600">Sortieren:</span>
        <select
          value={sortBy}
          onChange={(e) => onSortChange && onSortChange(e.target.value)}
          className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          aria-label="Sortierung auswählen"
        >
          <option value="name_asc">Name (A-Z)</option>
          <option value="name_desc">Name (Z-A)</option>
        </select>
      </div>
    </div>
  );
};

export default PlantCatalogHeader;
